/**
 * Durasan bundle / upsell — manually selected products for PDP, cart, and source-specific popup.
 * Discount itself is a Shopify automatic BXGY; this file only adds the offer product.
 */
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const STORAGE_KEY_FALLBACK = 'durasan:bundle-upsell:v1';

function config() {
  const node = document.getElementById('du-bundle-config');
  if (!node?.textContent) return {};
  try {
    return JSON.parse(node.textContent);
  } catch {
    return {};
  }
}

function sessionKey() {
  return config().storageKey || STORAGE_KEY_FALLBACK;
}

function seen() {
  try {
    return sessionStorage.getItem(sessionKey()) === '1';
  } catch {
    return true;
  }
}

function markSeen() {
  try {
    sessionStorage.setItem(sessionKey(), '1');
  } catch {
    /* private mode */
  }
}

function cartItems(detail) {
  return Array.isArray(detail?.items) ? detail.items : [];
}

function numericId(value) {
  return String(value || '').split('/').pop();
}

function isSourceAddition(event, cfg) {
  if (!cfg.sourceProductId || !Array.isArray(cfg.sourceVariantIds)) return false;
  const variants = new Set(cfg.sourceVariantIds.map(numericId));
  return Array.isArray(event.lines) && event.lines.some((line) => variants.has(numericId(line.merchandiseId)));
}

function cartAddUrl() {
  return (
    globalThis.Theme?.routes?.cart_add_url ||
    `${window.Shopify?.routes?.root || '/'}cart/add.js`
  );
}

function cartJsonUrl() {
  const cartUrl = globalThis.Theme?.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
  return `${cartUrl}.js`;
}

function sectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
}

let addingBundle = false;
let openingModal = false;

async function addVariant(variantId, button) {
  const id = Number(variantId);
  if (!id || addingBundle || button?.disabled) return;

  addingBundle = true;
  const original = button?.textContent;
  if (button) {
    button.disabled = true;
    button.textContent = button.dataset.addingLabel || 'Dodaję…';
  }

  const deferred = CartLinesUpdateEvent.createPromise();
  let didAdd = false;
  let cartSynced = false;
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(id), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const payload = {
      items: [{ id, quantity: 1 }],
      sections: sectionIds().join(','),
      sections_url: window.location.pathname,
    };
    const response = await fetch(cartAddUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.description || errorData.message || 'Cart request failed');
    }
    // A 2xx response means the server accepted the add. Preserve that fact
    // even if reading its JSON or refreshing the cart fails afterward.
    didAdd = true;
    const data = await response.json();
    if (data.status) {
      throw new Error(data.description || data.message || 'Cart response failed');
    }

    const cartResponse = await fetch(cartJsonUrl(), { headers: { Accept: 'application/json' } });
    if (!cartResponse.ok) throw new Error('Cart refresh failed');
    const ajaxCart = await cartResponse.json();
    cartSynced = true;
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        itemCount: ajaxCart.item_count,
        sections: data.sections,
        source: 'durasan-bundle',
        didError: false,
      },
    });

    if (button) button.textContent = button.dataset.addedLabel || 'Dodano';
    closeModal();
    openCartDrawer();
  } catch (error) {
    deferred.reject(error);
    if (didAdd) {
      // The add request succeeded. A failed cart refresh must not invite a
      // second click, which would add the same accessory twice.
      if (button) button.textContent = button.dataset.addedLabel || 'Dodano';
      closeModal();
      holdDrawer(false);
      window.location.assign(globalThis.Theme?.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`);
    } else {
      document.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Add to cart failed',
          code: 'INVALID',
        })
      );
      if (button) button.textContent = original || 'Dodaj';
    }
  } finally {
    addingBundle = false;
    if (!didAdd || cartSynced) {
      window.setTimeout(() => {
        if (!button) return;
        button.disabled = false;
        if (original) button.textContent = original;
      }, 1600);
    }
  }
}

function modalEl() {
  return document.getElementById('du-bundle-modal');
}

function holdDrawer(on) {
  if (on) document.documentElement.setAttribute('data-du-bundle-hold-drawer', '');
  else document.documentElement.removeAttribute('data-du-bundle-hold-drawer');
}

function openCartDrawer() {
  holdDrawer(false);
  const drawer = document.querySelector('#cart-drawer');
  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
    return;
  }
  document.querySelector('cart-icon, header-actions [href="/cart"]')?.click();
}

function closeCompetingDialogs(keep) {
  document.querySelectorAll('dialog[open]').forEach((dialog) => {
    if (dialog === keep) return;
    try {
      dialog.close();
    } catch {
      dialog.removeAttribute('open');
    }
  });
}

function dialogIsOpen(dialog) {
  return Boolean(
    dialog?.open ||
      dialog?.hasAttribute('open') ||
      dialog?.classList.contains('is-du-open')
  );
}

function forceOpenStyles(dialog) {
  dialog.style.setProperty('display', 'grid', 'important');
  dialog.style.setProperty('position', 'fixed', 'important');
  dialog.style.setProperty('inset', '0', 'important');
  dialog.style.setProperty('z-index', '2147483000', 'important');
  dialog.style.setProperty('width', '100vw', 'important');
  dialog.style.setProperty('min-width', '100vw', 'important');
  dialog.style.setProperty('height', '100dvh', 'important');
  dialog.style.setProperty('max-width', '100vw', 'important');
  dialog.style.setProperty('max-height', '100dvh', 'important');
}

function applyOpen(dialog) {
  dialog.setAttribute('open', '');
  dialog.classList.add('is-du-open');
  document.documentElement.classList.add('du-bundle-modal-open');
  forceOpenStyles(dialog);
}

function applyClosed(dialog) {
  dialog.classList.remove('is-du-open');
  document.documentElement.classList.remove('du-bundle-modal-open');
  dialog.style.removeProperty('display');
  dialog.style.removeProperty('width');
  dialog.style.removeProperty('min-width');
  dialog.style.removeProperty('height');
  dialog.style.removeProperty('max-width');
  dialog.style.removeProperty('max-height');
}

function tryShowDialog(dialog) {
  if (!dialog) return false;
  openingModal = true;
  closeCompetingDialogs(dialog);

  try {
    if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
    else if (!dialog.open) dialog.setAttribute('open', '');
  } catch {
    try {
      dialog.close?.();
    } catch {
      /* already closed */
    }
    closeCompetingDialogs(dialog);
    try {
      dialog.showModal?.();
    } catch {
      try {
        dialog.show?.();
      } catch {
        dialog.setAttribute('open', '');
      }
    }
  }

  applyOpen(dialog);
  openingModal = false;
  const box = dialog.getBoundingClientRect();
  return dialogIsOpen(dialog) || dialog.classList.contains('is-du-open') || box.width > 50;
}

function closeModal() {
  const dialog = modalEl();
  if (!dialog) {
    holdDrawer(false);
    return;
  }
  applyClosed(dialog);
  if (dialog.open) {
    try {
      dialog.close();
    } catch {
      dialog.removeAttribute('open');
    }
  } else {
    dialog.removeAttribute('open');
  }
  holdDrawer(false);
}

function waitFrame() {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

async function openModal() {
  const dialog = modalEl();
  if (!dialog || seen()) return false;
  holdDrawer(true);

  for (let i = 0; i < 10; i += 1) {
    if (tryShowDialog(dialog)) {
      markSeen();
      dialog.querySelector('.du-bundle__add--primary, [data-du-bundle-add]')?.focus?.();
      return true;
    }
    await waitFrame();
  }

  holdDrawer(false);
  return false;
}

async function maybeOpenFromCart(detail) {
  if (addingBundle || seen()) return false;
  const cfg = config();
  const dialog = modalEl();
  if (!cfg.sourceProductId || !dialog) return false;
  const items = cartItems(detail);
  const sourceId = numericId(cfg.sourceProductId);
  if (!items.some((item) => numericId(item.product_id) === sourceId)) return false;
  const offers = [...dialog.querySelectorAll('[data-du-bundle-offer-product]')];
  if (!offers.length) return false;
  // Avoid re-offering an accessory already purchased, even when other offers remain.
  const cartProducts = new Set(items.map((item) => numericId(item.product_id)));
  if (offers.some((offer) => cartProducts.has(numericId(offer.dataset.duBundleOfferProduct)))) return false;
  return openModal();
}

function onCartUpdate(event) {
  if (event.action !== 'add' || addingBundle || seen() || !modalEl() || !isSourceAddition(event, config())) return;
  holdDrawer(true);
  // Only an addition of this popup's exact source may delay the normal cart drawer.
  // Checking the full cart also prevents recommending an accessory already present.
  event.duBundleDecision = Promise.resolve(event.promise)
    .then(async (result) => {
      if (result?.detail?.didError || addingBundle || seen()) return false;
      const response = await fetch(cartJsonUrl());
      if (!response.ok) return false;
      return maybeOpenFromCart(await response.json());
    })
    .catch(() => false)
    .then((opened) => {
      if (!opened) holdDrawer(false);
      return opened;
    });
}

function syncPdpBundles() {
  // These lists are explicitly configured by the merchant and remain available
  // alongside size/colour pickers, including after a sibling-product navigation.
  document.querySelectorAll('.product-details .du-bundle--pdp').forEach((bundle) => {
    if (!(bundle instanceof HTMLElement)) return;
    bundle.hidden = false;
    bundle.setAttribute('data-du-force-show', '');
  });
}

/* --- Karuzela „W zestawie taniej” --------------------------------------
   Delegacja zamiast wiązania na elementach: koszyk i szuflada przerysowują
   swój HTML po każdej zmianie, więc każdy uchwyt zapięty na konkretnym
   przycisku i tak by przepadł. */

function bundleSlides(track) {
  return Array.from(track.querySelectorAll('[data-du-bundle-slide]'));
}

function bundleStep(track, slides) {
  const step = slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : 0;
  return step > 0 ? step : track.clientWidth || 1;
}

function syncBundleCarousel(track) {
  const root = track.closest('[data-du-bundle]');
  if (!root) return;
  const slides = bundleSlides(track);
  if (slides.length < 2) return;
  const index = Math.round(track.scrollLeft / bundleStep(track, slides));
  root.querySelectorAll('.du-bundle__dot').forEach((dot, i) => {
    dot.classList.toggle('is-active', i === index);
  });
  root.querySelectorAll('[data-du-bundle-prev]').forEach((b) => {
    b.disabled = index <= 0;
  });
  root.querySelectorAll('[data-du-bundle-next]').forEach((b) => {
    b.disabled = index >= slides.length - 1;
  });
}

function syncBundleCarousels() {
  document.querySelectorAll('[data-du-bundle-track]').forEach(syncBundleCarousel);
}

document.addEventListener(
  'scroll',
  (event) => {
    const track = event.target;
    if (!(track instanceof HTMLElement) || !track.matches?.('[data-du-bundle-track]')) return;
    syncBundleCarousel(track);
  },
  { capture: true, passive: true }
);

document.addEventListener('click', (event) => {
  const arrow = event.target.closest?.('[data-du-bundle-prev], [data-du-bundle-next]');
  if (arrow) {
    event.preventDefault();
    event.stopPropagation();
    const track = arrow.closest('[data-du-bundle]')?.querySelector('[data-du-bundle-track]');
    if (!track) return;
    const slides = bundleSlides(track);
    const step = bundleStep(track, slides);
    const dir = arrow.hasAttribute('data-du-bundle-next') ? 1 : -1;
    const target = Math.min(
      slides.length - 1,
      Math.max(0, Math.round(track.scrollLeft / step) + dir)
    );
    track.scrollTo({ left: target * step, behavior: 'smooth' });
    return;
  }

  const add = event.target.closest?.('[data-du-bundle-add]');
  if (add) {
    event.preventDefault();
    event.stopPropagation();
    addVariant(add.dataset.variantId, add);
    return;
  }
  if (event.target.closest?.('[data-du-bundle-dismiss]')) {
    event.preventDefault();
    markSeen();
    closeModal();
    openCartDrawer();
  }
});

document.addEventListener(StandardEvents.cartLinesUpdate, onCartUpdate, { capture: true });
document.addEventListener('durasan:sibling:changed', () => {
  window.requestAnimationFrame(syncPdpBundles);
});

const dialog = modalEl();
dialog?.addEventListener('cancel', (event) => {
  event.preventDefault();
  markSeen();
  closeModal();
  window.setTimeout(openCartDrawer, 80);
});
dialog?.addEventListener('close', () => {
  if (openingModal) return;
  applyClosed(dialog);
  if (!addingBundle) holdDrawer(false);
});
dialog?.addEventListener('click', (event) => {
  if (event.target === dialog) {
    markSeen();
    closeModal();
    openCartDrawer();
  }
});

function syncBundles() {
  syncPdpBundles();
  syncBundleCarousels();
}

syncBundles();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncBundles, { once: true });
}
window.addEventListener('pageshow', syncBundles);
window.addEventListener('resize', syncBundleCarousels);
document.addEventListener(StandardEvents.cartLinesUpdate, () => {
  window.setTimeout(syncBundleCarousels, 120);
  window.setTimeout(syncBundleCarousels, 600);
});
window.setTimeout(syncBundles, 50);
window.setTimeout(syncBundles, 400);
